import { useEffect, useRef, useState } from 'react';
import { GoogleMap, GoogleMapProps } from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import './styles.scss';

interface MapCoordinatesProps {
  onSelectCoordinates: (lat: number, lng: number) => void;
  address?: string;
  postalCode?: string;
  country: string;
  lat?: number;
  lng?: number;
}

export function MapCoordinates({ onSelectCoordinates, address, postalCode, country, lat, lng }: MapCoordinatesProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const isLoaded = useSelector<AppState>(state => state.map.isLoaded);
  const containerStyle = { width: '100%', height: '300px' };
  const defaultCenter = { lat: 38.73, lng: -9.14 };

  const [center, setCenter] = useState(defaultCenter);

  const createMarker = async (position: google.maps.LatLngLiteral) => {
    if (!mapRef.current) return;

    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

    if (markerRef.current) {
      markerRef.current.map = null;
    }

    const newMarker = new AdvancedMarkerElement({
      position,
      map: mapRef.current
    });

    newMarker.addListener('click', () => {
      onSelectCoordinates(position.lat, position.lng);
    });

    markerRef.current = newMarker;
  };

  useEffect(() => {
    if (!isLoaded) return;

    if (lat && lng) {
      const position = { lat, lng };
      setCenter(position);
      createMarker(position);
      onSelectCoordinates(lat, lng);
      return;
    }

    let query = '';
    if (address) {
      query = `${address}, ${postalCode || ''}, ${country}`;
    } else if (postalCode && country) {
      query = `${postalCode}, ${country}`;
    } else {
      return;
    }

    async function fetchCoordinatesFromNominatim(address: string) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
            address
          )}&format=json&limit=1&addressdetails=1`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);

          const position = { lat, lng };
          setCenter(position);
          createMarker(position);
          onSelectCoordinates(lat, lng);
          mapRef.current?.panTo(position);
        } else {
          console.warn('Nominatim: No results found for address:', address);
        }
      } catch (error) {
        console.error('Nominatim geocoding error:', error);
      }
    }

    fetchCoordinatesFromNominatim(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, address, postalCode, country, isLoaded, onSelectCoordinates]);

  if (!isLoaded) return <div>Loading map...</div>;

  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    await createMarker(position);
    onSelectCoordinates(position.lat, position.lng);
    mapRef.current?.panTo(position);
  };

  const mapOptions: GoogleMapProps['options'] = {
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'DEMO_MAP_ID',
    draggableCursor: 'pointer',
    clickableIcons: false
  };
  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={13}
      onClick={handleMapClick}
      options={mapOptions}
      onLoad={map => {
        mapRef.current = map;
        if (lat && lng) {
          createMarker({ lat, lng });
        }
      }}
    />
  );
}
